import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import './Testimonials.css';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    testimonial: '',
    rating: 5
  });
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const q = query(
        collection(db, 'testimonials'),
        orderBy('createdAt', 'desc'),
        limit(8)
      );
      const querySnapshot = await getDocs(q);
      const testimonialsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTestimonials(testimonialsData);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await addDoc(collection(db, 'testimonials'), {
        ...formData,
        createdAt: serverTimestamp(),
        approved: true // Auto-approve for demo
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        company: '',
        testimonial: '',
        rating: 5
      });

      setTimeout(() => {
        setSubmitSuccess(false);
        setIsModalOpen(false);
      }, 2000);

      fetchTestimonials();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSubmitSuccess(false);
  };

  return (
    <section className="testimonials-premium">
      {/* Decorative Elements */}
      <div className="premium-bg-pattern"></div>
      <div className="premium-accent-line"></div>
      
      <div className="testimonials-premium-container">
        {/* Header Section */}
        <div className="testimonials-premium-header">
        <h2 className="premium-title">
            What Our <span className="accent-text">Clients Say</span>
          </h2>
          <span className="premium-subtitle">Client Testimonials</span>
          
          <p className="premium-description">
            Discover why leading businesses trust Kajja Galleries for their premium needs
          </p>
        </div>

        {/* Luxury Testimonials Grid */}
        <div className="testimonials-luxury-grid">
          {testimonials.slice(0, 4).map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className={`luxury-card ${index === 0 ? 'featured' : ''}`}
            >
              <div className="card-inner">
                <div className="card-front">
                  <div className="quote-mark">“</div>
                  <div className="rating-badge">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={`star ${i < testimonial.rating ? 'filled' : ''}`}>★</span>
                    ))}
                  </div>
                  <p className="testimonial-excerpt">
                    {testimonial.testimonial.length > 120 
                      ? `${testimonial.testimonial.substring(0, 120)}...` 
                      : testimonial.testimonial}
                  </p>
                  <div className="client-info">
                    <div className="client-avatar">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="client-details">
                      <h4>{testimonial.name}</h4>
                      <p>{testimonial.company || 'Client'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Marquee / Carousel for More Testimonials */}
        {testimonials.length > 4 && (
          <div className="testimonials-marquee">
            <div className="marquee-content">
              {[...testimonials, ...testimonials].map((testimonial, index) => (
                <div key={`${testimonial.id}-${index}`} className="marquee-item">
                  <span className="marquee-quote">"{testimonial.testimonial.substring(0, 60)}..."</span>
                  <span className="marquee-author">— {testimonial.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Button */}
        <div className="testimonials-cta">
          <button onClick={openModal} className="premium-cta-button">
            <span>Share Your Testimonial</span>
            <svg className="button-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Premium Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="premium-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            
            {submitSuccess ? (
              <div className="success-state">
                <div className="success-animation">
                  <svg className="checkmark" viewBox="0 0 52 52">
                    <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                    <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                  </svg>
                </div>
                <h3>Thank You!</h3>
                <p>Your testimonial has been submitted successfully</p>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h2>Share Your Story</h2>
                  <p>We value your feedback and appreciate your support</p>
                </div>

                <form onSubmit={handleSubmit} className="premium-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Company (Optional)</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your business name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Your Rating *</label>
                    <div className="premium-rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`rating-star ${star <= formData.rating ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                        >
                          ★
                        </button>
                      ))}
                      <span className="rating-text">
                        {formData.rating === 5 ? 'Excellent' :
                         formData.rating === 4 ? 'Very Good' :
                         formData.rating === 3 ? 'Good' :
                         formData.rating === 2 ? 'Fair' : 'Poor'}
                      </span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Your Testimonial *</label>
                    <textarea
                      name="testimonial"
                      value={formData.testimonial}
                      onChange={handleInputChange}
                      required
                      rows="5"
                      placeholder="Tell us about your experience..."
                    />
                    <span className="char-count">
                      {formData.testimonial.length}/500
                    </span>
                  </div>

                  <button 
                    type="submit" 
                    className="premium-submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="loading-spinners"></span>
                    ) : (
                      'Submit Testimonial'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default Testimonials;